import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import Logo from "@/components/Logo";

interface PageLoaderProps {
  message?: string;
  showLogo?: boolean;
}

/**
 * PageLoader - Full-page loading spinner for Suspense boundaries
 */
export function PageLoader({ 
  message = "Loading...",
  showLogo = true 
}: PageLoaderProps) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background">
      {showLogo && (
        <div className="animate-pulse">
          <Logo />
        </div>
      )}
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="text-sm">{message}</span>
      </div>
    </div>
  );
}

interface SpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

/**
 * Spinner - Inline loading spinner
 */
export function Spinner({ size = "md", className }: SpinnerProps) {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8",
  };

  return (
    <Loader2 
      className={cn(
        "animate-spin text-primary",
        sizeClasses[size],
        className
      )} 
    />
  );
}

interface LoadingOverlayProps {
  message?: string;
  progress?: number;
}

/**
 * LoadingOverlay - Overlay for processing states (e.g., AI generation)
 */
export function LoadingOverlay({ 
  message = "Processing...",
  progress 
}: LoadingOverlayProps) {
  return (
    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4 z-50 animate-in fade-in-50 duration-200">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-sm font-medium">{message}</p>
        {progress !== undefined && (
          <div className="w-48">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${Math.min(100, progress)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center mt-1">
              {Math.round(progress)}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ButtonLoader - Loading state for buttons
 */
export function ButtonLoader({ children }: { children?: React.ReactNode }) {
  return (
    <>
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      {children || "Loading..."}
    </>
  );
}
